import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../config/supabase';
import { useSupabaseSession } from './session';
import {
  hasPermission as hasPerm,
  type PortalAccess,
} from './permissions';

export interface UsePortalPermissionsResult {
  access: PortalAccess | null;
  permissions: string[];
  loading: boolean;
  error: string | null;
  hasPermission: (code: string) => boolean;
  refresh: () => Promise<void>;
}

function parseAccess(raw: unknown): PortalAccess | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const role = obj.role as Record<string, unknown> | undefined;
  if (!role || typeof role.id !== 'string') return null;
  const permissions = Array.isArray(obj.permissions)
    ? obj.permissions.filter((p): p is string => typeof p === 'string')
    : [];
  return {
    auth_uid: String(obj.auth_uid ?? ''),
    status: obj.status === 'disabled' ? 'disabled' : 'active',
    display_name: typeof obj.display_name === 'string' ? obj.display_name : null,
    role: {
      id: String(role.id),
      code: String(role.code ?? ''),
      name: String(role.name ?? ''),
      is_system: Boolean(role.is_system),
    },
    permissions,
    fleet_operator_id:
      obj.fleet_operator_id === null || obj.fleet_operator_id === undefined
        ? null
        : String(obj.fleet_operator_id),
    is_primary_manager: Boolean(obj.is_primary_manager),
  };
}

export function usePortalPermissions(): UsePortalPermissionsResult {
  const { session, loading: sessionLoading } = useSupabaseSession();
  const [access, setAccess] = useState<PortalAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!session?.user) {
      setAccess(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    const { data, error: rpcError } = await supabase.rpc('get_my_portal_access');
    if (rpcError) {
      setError(rpcError.message);
      setAccess(null);
      setLoading(false);
      return;
    }

    const parsed = parseAccess(data);
    setAccess(parsed);
    setError(null);
    setLoading(false);
  }, [session?.user?.id]);

  useEffect(() => {
    if (sessionLoading) return;
    void refresh();
  }, [sessionLoading, refresh]);

  const permissions = access?.permissions ?? [];

  const hasPermission = useCallback(
    (code: string) => {
      if (access?.status === 'disabled') return false;
      return hasPerm(permissions, code);
    },
    [access?.status, permissions]
  );

  return useMemo(
    () => ({
      access,
      permissions,
      loading: sessionLoading || loading,
      error,
      hasPermission,
      refresh,
    }),
    [access, permissions, sessionLoading, loading, error, hasPermission, refresh]
  );
}
